import type { ReactNode } from 'react'
import { Link } from 'react-router'
import styles from './ActionLink.module.css'

interface ActionLinkProps {
  to: string
  children: ReactNode
  /**
   * `primary` carries the brand gradient — at most one per section, on the
   * thing the visitor is meant to press. `secondary` is a neutral outline.
   */
  variant?: 'primary' | 'secondary'
  className?: string
}

/**
 * A link that looks like a button.
 *
 * Nexus `Button` renders a real `<button>` and is not polymorphic, but every
 * primary action on the discovery pages is navigation. Wrapping a Link in a
 * Button swallows the click; styling the Link keeps it a link.
 */
export function ActionLink({ to, children, variant = 'primary', className }: ActionLinkProps) {
  const classes = [styles.action, styles[variant], className].filter(Boolean).join(' ')
  return <Link to={to} className={classes}>{children}</Link>
}

interface ActionButtonProps {
  onClick: () => void
  children: ReactNode
  variant?: 'primary' | 'secondary'
  className?: string
}

/**
 * ActionLink's twin for the one action on a discovery page that is not
 * navigation — opening a dialog. Same look, so a row of actions stays even.
 */
export function ActionButton({ onClick, children, variant = 'primary', className }: ActionButtonProps) {
  const classes = [styles.action, styles.button, styles[variant], className].filter(Boolean).join(' ')
  return <button type="button" onClick={onClick} className={classes}>{children}</button>
}
